import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    DropzoneDragStyleConstantType,
    DropzoneColorsConstantType,
    DropzoneDynamicStyleType,
    DropzoneColors
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    FileUploadSize
} from "../../type";
import {
    getFieldFocusStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    webStyle
} from "../../../../utils";

export const DROPZONE_PADDINGS: Record<FileUploadSize, keyof NCoreUIKit.Spaces> = {
    medium: "spacingLg",
    small: "spacingMd",
    large: "spacingXl"
};

export const DROPZONE_ACTIVE_TITLE_VARIANTS: Record<FileUploadSize, keyof NCoreUIKit.Typography> = {
    large: "titleMediumSize",
    medium: "titleSmallSize",
    small: "titleSmallSize"
};

export const DROPZONE_DESCRIPTION_VARIANTS: Record<FileUploadSize, keyof NCoreUIKit.Typography> = {
    large: "bodyMediumSize",
    medium: "bodySmallSize",
    small: "bodySmallSize"
};

export const getDropzoneColors = ({
    isInteracted,
    isDisabled,
    isDragOver,
    isInvalid,
    colors
}: DropzoneColorsConstantType): DropzoneColors => {
    const colorsBase: DropzoneColors = {
        iconBackgroundColor: colors.content.container.subtle,
        backgroundColor: colors.content.container.raised,
        borderColor: colors.content.border.default,
        descriptionColor: colors.content.text.mid,
        titleColor: colors.content.text.high,
        linkColor: colors.content.text.link,
        iconColor: colors.content.icon.mid,
        borderStyle: "dashed"
    };

    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            ...colorsBase,
            backgroundColor: colors.system.state.overlay.disabled.neutral,
            iconBorderColor: colors.system.state.border.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral,
            iconBackgroundColor: "transparent",
            descriptionColor: disabledColor,
            titleColor: disabledColor,
            linkColor: disabledColor,
            iconColor: disabledColor
        };
    }

    if(isDragOver) {
        return {
            ...colorsBase,
            iconBackgroundColor: colors.content.container.raised,
            backgroundColor: colors.content.container.selected,
            borderColor: colors.content.text.high,
            iconColor: colors.content.icon.high,
            borderStyle: "solid"
        };
    }

    if(isInvalid) {
        return {
            ...colorsBase,
            iconBackgroundColor: colors.system.low.error,
            borderColor: colors.content.border.danger,
            iconColor: colors.content.icon.danger
        };
    }

    if(isInteracted) {
        return {
            ...colorsBase,
            borderColor: colors.content.border.hover
        };
    }

    return colorsBase;
};

export const getDropzoneDragStyle = ({
    borders,
    color
}: DropzoneDragStyleConstantType): ViewStyle => {
    return {
        boxShadow: [
            {
                spreadDistance: borders.focus - borders.line,
                inset: true,
                offsetX: 0,
                offsetY: 0,
                color
            }
        ]
    };
};

const stylesheet = StyleSheet.create({
    inline: {
        ...webStyle({
            display: "inline-flex" as "flex",
            verticalAlign: "middle"
        })
    },
    link: {
        textDecorationLine: "underline"
    },
    container: {
        alignSelf: "stretch",
        ...webStyle({
            outlineStyle: "solid",
            cursor: "pointer",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center"
    },
    icon: {
        justifyContent: "center",
        alignItems: "center"
    },
    disabledCursor: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    description: {
        alignItems: "center"
    },
    tabular: {
        textAlign: "center",
        fontVariant: [
            "tabular-nums"
        ]
    },
    title: {
        textAlign: "center"
    }
});

export const useStyles = ({
    controlHeights,
    currentColors,
    borderRadius,
    shapeConfigs,
    isDragOver,
    isInvalid,
    borders,
    colors,
    spaces,
    size
}: DropzoneDynamicStyleType) => {
    const styles = {
        icon: {
            backgroundColor: currentColors.iconBackgroundColor,
            borderRadius: controlHeights[size],
            height: controlHeights[size],
            width: controlHeights[size]
        } as Mutable<ViewStyle>,
        surface: {
            paddingVertical: spaces[DROPZONE_PADDINGS[size]],
            backgroundColor: currentColors.backgroundColor,
            borderStyle: currentColors.borderStyle,
            borderColor: currentColors.borderColor,
            paddingHorizontal: spaces.spacingMd,
            borderWidth: borders.line,
            rowGap: spaces.spacingSm,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>
    };

    if(currentColors.iconBorderColor) {
        Object.assign(styles.icon, {
            borderColor: currentColors.iconBorderColor,
            borderWidth: borders.line
        });
    }

    if(isDragOver) {
        Object.assign(styles.surface, getDropzoneDragStyle({
            color: currentColors.borderColor,
            borders
        }));
    }

    if(isInvalid && !isDragOver) {
        Object.assign(styles.surface, getFieldFocusStyle({
            isFocused: false,
            isError: true,
            borders,
            colors
        }));
    }

    return styles;
};
export default stylesheet;
